import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { LINKS, scrollToSection } from "../config";
import { useLang } from "../i18n";

const NAV = [
  { key: "story", href: "#story" },
  { key: "menu", href: "#menu" },
  { key: "picks", href: "#picks" },
  { key: "gallery", href: "#gallery" },
  { key: "reviews", href: "#reviews" },
  { key: "visit", href: "#visit" },
];

export const Navbar = () => {
  const { t, isAr, toggle } = useLang();
  const [open, setOpen] = useState(false);

  const go = (href) => (e) => {
    setOpen(false);
    scrollToSection(href)(e);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-paper/85 backdrop-blur-xl border-b border-line"
      data-testid="main-navbar"
    >
      <div className="px-6 md:px-12 max-w-[1400px] mx-auto h-[76px] flex items-center justify-between gap-4">
        <a href="#top" onClick={go("#top")} className="group shrink-0" data-testid="nav-logo" aria-label="Sipsation home">
          <Logo />
        </a>
        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-links">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={go(n.href)}
              data-testid={`nav-link-${n.key}`}
              className="text-[11px] uppercase tracking-[0.22em] text-smoke hover:text-pine transition-colors duration-300"
            >
              {t.nav[n.key]}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle-button"
            className={`text-[11px] uppercase tracking-[0.18em] border border-pine/30 text-pine px-3.5 sm:px-4 py-2.5 sm:py-3 rounded-none hover:border-gold hover:text-gold transition-colors duration-300 ${
              isAr ? "font-body" : "font-arabicbody"
            }`}
            aria-label="Switch language"
          >
            {isAr ? "EN" : "عربي"}
          </button>
          <a
            href={LINKS.talabat}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-order-button"
            className="hidden sm:inline-block text-[11px] uppercase tracking-[0.22em] bg-pine text-paper px-6 py-3 rounded-none hover:bg-forest transition-colors duration-300"
          >
            {t.nav.order}
          </a>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="lg:hidden text-pine p-2.5 -me-1 hover:text-gold transition-colors duration-300"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-paper border-t border-line"
            data-testid="mobile-nav"
          >
            <div className="px-6 py-4 flex flex-col">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={go(n.href)}
                  data-testid={`mobile-nav-link-${n.key}`}
                  className="py-4 border-b border-line/70 text-sm uppercase tracking-[0.22em] text-pine hover:text-gold transition-colors duration-300"
                >
                  {t.nav[n.key]}
                </a>
              ))}
              <a
                href={LINKS.talabat}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-order"
                className="mt-5 text-center text-xs uppercase tracking-[0.22em] bg-pine text-paper px-6 py-4 rounded-none hover:bg-forest transition-colors duration-300"
              >
                {t.nav.order}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
